import { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import type { IndexNode } from '../types';

interface IndexTreeBrowserProps {
  root: IndexNode;
  selectedId: string | null;
  onSelect: (node: IndexNode) => void;
}

function collectIds(node: IndexNode): string[] {
  const ids: string[] = [];
  const stack: IndexNode[] = [node];
  while (stack.length > 0) {
    const current = stack.pop()!;
    ids.push(current.id);
    for (const child of current.children) stack.push(child);
  }
  return ids;
}

const EXPAND_ALL_THRESHOLD = 2000;

function FolderIcon({ open }: { open: boolean }) {
  return open ? (
    <svg className="h-3.5 w-3.5 shrink-0 text-warn" viewBox="0 0 20 20" fill="currentColor">
      <path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v1H2V6zm0 4h16v6a2 2 0 01-2 2H4a2 2 0 01-2-2v-6z" />
    </svg>
  ) : (
    <svg className="h-3.5 w-3.5 shrink-0 text-warn/70" viewBox="0 0 20 20" fill="currentColor">
      <path d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg className="h-3.5 w-3.5 shrink-0 text-dim" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
        clipRule="evenodd"
      />
    </svg>
  );
}

interface TreeItemProps {
  node: IndexNode;
  depth: number;
  selectedId: string | null;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (node: IndexNode) => void;
  isLast: boolean;
}

function TreeItem({
  node,
  depth,
  selectedId,
  expandedIds,
  onToggle,
  onSelect,
  isLast,
}: TreeItemProps) {
  const isExpanded = expandedIds.has(node.id);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        className={cn(
          'relative flex items-center group cursor-pointer rounded transition-colors',
          isSelected ? 'bg-accent-soft' : 'hover:bg-bg'
        )}
        style={{ paddingLeft: `${depth * 12}px` }}
        onClick={() => onSelect(node)}
      >
        {/* Vertical connector from parent */}
        {depth > 0 && (
          <span
            className="pointer-events-none absolute top-0 bottom-0 border-l border-rule"
            style={{ left: `${depth * 12 - 6}px` }}
          />
        )}
        {/* Horizontal connector */}
        {depth > 0 && (
          <span
            className="pointer-events-none absolute top-[50%] border-t border-rule"
            style={{ left: `${depth * 12 - 6}px`, width: '6px' }}
          />
        )}
        {/* Stop vertical line after last child */}
        {depth > 0 && isLast && (
          <span
            className="pointer-events-none absolute top-[50%] bottom-0 bg-panel"
            style={{ left: `${depth * 12 - 7}px`, width: '2px' }}
          />
        )}

        {/* Chevron (only if has children) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(node.id);
          }}
          className={cn(
            'mr-1 flex h-4 w-4 shrink-0 items-center justify-center rounded transition-colors',
            hasChildren ? 'text-dim hover:text-link' : 'pointer-events-none opacity-0'
          )}
        >
          <svg
            className={cn('h-3 w-3 transition-transform', isExpanded && 'rotate-90')}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Icon */}
        <span className="mr-1.5">
          {hasChildren ? <FolderIcon open={isExpanded} /> : <FileIcon />}
        </span>

        {/* Label */}
        <span
          title={node.summary ?? node.title}
          className={cn(
            'flex-1 min-w-0 truncate py-0.5 px-1 text-left text-xs',
            isSelected ? 'text-accent font-medium' : 'text-link'
          )}
        >
          {node.title}
        </span>

        {/* Node id badge on hover */}
        <span className="ml-1 hidden shrink-0 rounded bg-bg px-1 py-0.5 font-mono text-3xs text-dim group-hover:inline">
          {node.id}
        </span>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child, i) => (
            <TreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onSelect={onSelect}
              isLast={i === node.children.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function IndexTreeBrowser({ root, selectedId, onSelect }: IndexTreeBrowserProps) {
  const rootIds = useMemo(() => collectIds(root), [root]);
  const isLargeTree = rootIds.length > EXPAND_ALL_THRESHOLD;

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    // Lazy: only expand root and its immediate children. Expanding every node
    // on mount makes huge trees crash the page.
    const initial = new Set<string>([root.id]);
    for (const child of root.children) initial.add(child.id);
    return initial;
  });

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandNextLevel = () => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const stack: IndexNode[] = [root];
      while (stack.length > 0) {
        const current = stack.pop()!;
        if (next.has(current.id)) {
          for (const child of current.children) {
            next.add(child.id);
            stack.push(child);
          }
        }
      }
      return next;
    });
  };

  const expandAll = () => {
    if (isLargeTree) {
      const ok = window.confirm(
        `This tree has ${rootIds.length.toLocaleString()} nodes. Expanding them all may freeze the page. Continue?`
      );
      if (!ok) return;
    }
    setExpandedIds(new Set(rootIds));
  };

  return (
    <div className="lc-surface overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-rule-soft bg-bg px-4 py-2.5">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-dim">Page index</h3>
          <p className="text-2xs text-dim">
            {rootIds.length.toLocaleString()} nodes{isLargeTree ? ' · expand subtrees lazily' : ''}
          </p>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={expandNextLevel}
            className="rounded px-2 py-1 text-2xs font-medium text-link hover:bg-bg"
            title="Expand one more level below currently open nodes"
          >
            Expand +1
          </button>
          <button
            type="button"
            onClick={expandAll}
            className="rounded px-2 py-1 text-2xs font-medium text-accent hover:bg-accent-soft"
            title={
              isLargeTree ? 'Expand all (may be slow on very large trees)' : 'Expand all nodes'
            }
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setExpandedIds(new Set([root.id]))}
            className="rounded px-2 py-1 text-2xs font-medium text-dim hover:bg-bg"
          >
            Collapse
          </button>
        </div>
      </div>

      {/* Tree */}
      <div className="max-h-[70vh] overflow-y-auto py-2 pl-3 pr-2 font-mono">
        <TreeItem
          node={root}
          depth={0}
          selectedId={selectedId}
          expandedIds={expandedIds}
          onToggle={toggleExpanded}
          onSelect={onSelect}
          isLast={true}
        />
      </div>
    </div>
  );
}
